import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { LinkGroup } from "@/components/CellLinkDropdown";
import {
  CombinedResult,
  SortOption,
  MAX_LENGTH,
  calculatePossibleLetters,
} from "@/lib/advancedSearchTypes";

/**
 * Re-run a search for a tab using its saved parameters.
 * Called on load for tabs where has_searched is true.
 */
async function rerunSearchForTab(tab: AdvancedSearchTab): Promise<CombinedResult[]> {
  try {
    // Build letter pattern from saved letters + selectedLength
    let letterPattern: string | null = null;
    if (tab.selectedLength && tab.letters.some(l => l.trim() !== "")) {
      letterPattern = tab.letters.slice(0, tab.selectedLength).map(l => l || "?").join("");
    }

    // Need at least a pattern or a clue
    if (!letterPattern && !tab.clueQuery.trim()) return [];

    const { data, error } = await supabase.functions.invoke('advanced-search', {
      body: {
        letterPattern,
        clueQuery: tab.clueQuery.trim(),
        selectedLength: tab.selectedLength,
        languageFilter: tab.languageFilter,
        constraintPositions: {}
      }
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return (data?.results || []).map((r: any) => ({
      word: r.word,
      wordLength: r.wordLength,
      clues: r.clues || [],
      allClues: r.allClues || [],
      matchPercentage: r.matchPercentage,
      language: r.language
    }));
  } catch (err) {
    console.error("Error re-running search for tab:", tab.name, err);
    return [];
  }
}

export interface AdvancedSearchTab {
  id: string;
  name: string;
  clueQuery: string;
  selectedLength: number | null;
  letters: string[];
  results: CombinedResult[];
  hasSearched: boolean;
  isLoading: boolean;
  error: string | null;
  sortOption: SortOption;
  currentPage: number;
  languageFilter: number;
  possibleLetters: Map<number, Set<string>>;
  isLocal?: boolean;
  lastSeenFilteredCount?: number; // Session-only: last seen filtered result count (for bold indicator)
}

// Database row types
interface DbAdvancedSearch {
  id: string;
  user_id: string;
  name: string;
  clue_query: string;
  selected_length: number | null;
  letters: string[];
  results: CombinedResult[];
  has_searched: boolean;
  sort_option: string;
  language_filter: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface DbLinkGroup {
  id: string;
  user_id: string;
  group_id: string;
  color: string;
  cells: Array<{ tabId: string; cellIndex: number }>;
  created_at: string;
  updated_at: string;
}

function createNewTab(): AdvancedSearchTab {
  return {
    id: crypto.randomUUID(),
    name: "",
    clueQuery: "",
    selectedLength: null,
    letters: Array(MAX_LENGTH).fill(""),
    results: [],
    hasSearched: false,
    isLoading: false,
    error: null,
    sortOption: "relevance",
    currentPage: 1,
    languageFilter: 1, // Swedish default
    possibleLetters: new Map(),
  };
}

function createLocalTab(): AdvancedSearchTab {
  return {
    ...createNewTab(),
    id: `local-${Date.now()}`,
    isLocal: true,
  };
}

export const useAdvancedSearchTabs = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [tabs, setTabs] = useState<AdvancedSearchTab[]>([createLocalTab()]);
  const [activeTabId, setActiveTabId] = useState<string>(tabs[0].id);
  const [linkGroups, setLinkGroups] = useState<LinkGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadDone = useRef(false);

  // Refs to avoid stale closures in save functions (1.6)
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const linkGroupsRef = useRef(linkGroups);
  linkGroupsRef.current = linkGroups;

  // Load tabs and links from database for logged-in users
  useEffect(() => {
    const loadData = async () => {
      // Wait for auth to finish loading before deciding anything
      if (authLoading) return;

      // Skip reload if already done - prevents resetting activeTabId
      if (initialLoadDone.current) {
        return;
      }
      
      if (!isAuthenticated || !user) {
        // Non-logged-in users get a local tab
        const localTab = createLocalTab();
        setTabs([localTab]);
        setActiveTabId(localTab.id);
        setLinkGroups([]);
        setIsLoading(false);
        initialLoadDone.current = true;
        return;
      }

      try {
        // Load tabs
        const { data: tabsData, error: tabsError } = await supabase
          .from("user_advanced_searches")
          .select("*")
          .order("sort_order", { ascending: true });

        if (tabsError) throw tabsError;

        // Load link groups
        const { data: linksData, error: linksError } = await supabase
          .from("user_advanced_search_links")
          .select("*");

        if (linksError) throw linksError;

        const rows = tabsData as unknown as DbAdvancedSearch[];
        const linkRows = linksData as unknown as DbLinkGroup[];

        if (rows && rows.length > 0) {
          const loadedTabs: AdvancedSearchTab[] = rows.map((row) => ({
            id: row.id,
            name: row.name,
            clueQuery: row.clue_query,
            selectedLength: row.selected_length,
            letters: Array.isArray(row.letters) ? row.letters : Array(MAX_LENGTH).fill(""),
            results: [], // Don't load results from DB - will re-search
            hasSearched: row.has_searched,
            isLoading: row.has_searched, // Show loading for tabs that need re-search
            error: null,
            sortOption: (row.sort_option as SortOption) || "relevance",
            currentPage: 1,
            languageFilter: row.language_filter,
            possibleLetters: new Map(),
          }));
          setTabs(loadedTabs);
          setActiveTabId(loadedTabs[0].id);

          // Re-run searches for tabs that had results (async, after render)
          const tabsToSearch = loadedTabs.filter(t => t.hasSearched);
          if (tabsToSearch.length > 0) {
            // Fire off all re-searches in parallel
            Promise.all(tabsToSearch.map(async (tab) => {
              const results = await rerunSearchForTab(tab);
              setTabs(prev => prev.map(t =>
                t.id === tab.id
                  ? { ...t, results, isLoading: false, possibleLetters: calculatePossibleLetters(results) }
                  : t
              ));
            })).catch(err => console.error("Error re-running searches:", err));
          }
        } else {
          // Create first tab for new users
          const newTab = createNewTab();
          const { data: insertedData, error: insertError } = await supabase
            .from("user_advanced_searches")
            .insert({
              id: newTab.id,
              user_id: user.id,
              name: newTab.name,
              clue_query: newTab.clueQuery,
              selected_length: newTab.selectedLength,
              letters: newTab.letters as unknown as null,
              results: newTab.results as unknown as null,
              has_searched: newTab.hasSearched,
              sort_option: newTab.sortOption,
              language_filter: newTab.languageFilter,
              sort_order: 0,
            } as any)
            .select()
            .single();

          if (insertError) throw insertError;

          const insertedRow = insertedData as unknown as DbAdvancedSearch;
          setTabs([{
            ...newTab,
            id: insertedRow.id,
          }]);
          setActiveTabId(insertedRow.id);
        }

        // Load link groups
        if (linkRows && linkRows.length > 0) {
          const loadedLinks: LinkGroup[] = linkRows.map((row) => ({
            id: row.group_id,
            color: row.color,
            cells: Array.isArray(row.cells) ? row.cells : [],
          }));
          setLinkGroups(loadedLinks);
        }
      } catch (error) {
        console.error("Error loading advanced search tabs:", error);
        // Fallback to local tab on error
        const localTab = createLocalTab();
        setTabs([localTab]);
        setActiveTabId(localTab.id);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };

    loadData();
  }, [isAuthenticated, user, authLoading]);

  // Debounced save function
  const saveToDatabase = useCallback(async (
    tabsToSave: AdvancedSearchTab[],
    linksToSave: LinkGroup[]
  ) => {
    if (!isAuthenticated || !user || !initialLoadDone.current) return;

    // Filter out local tabs
    const persistentTabs = tabsToSave.filter(t => !t.isLocal);
    if (persistentTabs.length === 0) return;

    setIsSaving(true);
    try {
      // Save tabs - delete all then batch insert (prevents orphaned rows)
      const { error: deleteError } = await supabase
        .from("user_advanced_searches")
        .delete()
        .eq("user_id", user.id);

      if (deleteError) {
        console.error("Delete failed, skipping insert to prevent duplicates:", deleteError);
        return;
      }

      if (persistentTabs.length > 0) {
        const inserts = persistentTabs.map((tab, i) => ({
          id: tab.id,
          user_id: user.id,
          name: tab.name || "Ny",
          clue_query: tab.clueQuery,
          selected_length: tab.selectedLength,
          letters: tab.letters as unknown as null,
          results: [] as unknown as null, // Never persist results - re-searched on load
          has_searched: tab.hasSearched,
          sort_option: tab.sortOption,
          language_filter: tab.languageFilter,
          sort_order: i,
        }));

        const { error: insertError } = await supabase
          .from("user_advanced_searches")
          .insert(inserts as any);

        if (insertError) {
          console.error("Insert failed after delete:", insertError);
        }
      }

      // Save link groups - delete all and re-insert
      await supabase
        .from("user_advanced_search_links")
        .delete()
        .eq("user_id", user.id);

      if (linksToSave.length > 0) {
        const linkInserts = linksToSave.map(link => ({
          user_id: user.id,
          group_id: link.id,
          color: link.color,
          cells: link.cells,
        }));

        await supabase
          .from("user_advanced_search_links")
          .insert(linkInserts);
      }
    } catch (error) {
      console.error("Error saving advanced search tabs:", error);
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, user]);

  // Debounced save trigger
  const triggerSave = useCallback((newTabs: AdvancedSearchTab[], newLinks: LinkGroup[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToDatabase(newTabs, newLinks);
    }, 1000); // 1 second debounce
  }, [saveToDatabase]);

  // Update tabs with auto-save (1.6: uses linkGroupsRef to avoid stale closure)
  const updateTabs = useCallback((
    updater: (prev: AdvancedSearchTab[]) => AdvancedSearchTab[]
  ) => {
    setTabs(prev => {
      const newTabs = updater(prev);
      triggerSave(newTabs, linkGroupsRef.current);
      return newTabs;
    });
  }, [triggerSave]);

  // Update link groups with auto-save (1.6: uses tabsRef to avoid stale closure)
  const updateLinkGroups = useCallback((
    updater: (prev: LinkGroup[]) => LinkGroup[]
  ) => {
    setLinkGroups(prev => {
      const newLinks = updater(prev);
      triggerSave(tabsRef.current, newLinks);
      return newLinks;
    });
  }, [triggerSave]);

  // Add new tab
  const addTab = useCallback(async (): Promise<AdvancedSearchTab> => {
    const newTab = isAuthenticated && user ? createNewTab() : createLocalTab();
    
    if (isAuthenticated && user) {
      try {
        const { data, error } = await supabase
          .from("user_advanced_searches")
          .insert({
            id: newTab.id,
            user_id: user.id,
            name: newTab.name,
            clue_query: newTab.clueQuery,
            selected_length: newTab.selectedLength,
            letters: newTab.letters as unknown as null,
            results: [] as unknown as null,
            has_searched: false,
            sort_option: newTab.sortOption,
            language_filter: newTab.languageFilter,
            sort_order: tabs.length,
          } as any)
          .select()
          .single();

        if (error) throw error;

        const row = data as unknown as DbAdvancedSearch;
        const insertedTab: AdvancedSearchTab = {
          ...newTab,
          id: row.id,
        };
        
        setTabs(prev => [...prev, insertedTab]);
        return insertedTab;
      } catch (error) {
        console.error("Error creating tab:", error);
        // Fall back to local tab
        const localTab = createLocalTab();
        setTabs(prev => [...prev, localTab]);
        return localTab;
      }
    } else {
      setTabs(prev => [...prev, newTab]);
      return newTab;
    }
  }, [isAuthenticated, user, tabs.length]);

  // Delete tab
  const deleteTab = useCallback(async (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;

    // Remove from link groups
    setLinkGroups(prev => {
      const updated = prev
        .map(g => ({
          ...g,
          cells: g.cells.filter(c => c.tabId !== tabId)
        }))
        .filter(g => g.cells.length > 1); // Remove groups with less than 2 cells
      
      triggerSave(tabs.filter(t => t.id !== tabId), updated);
      return updated;
    });

    // If this is the last tab, create a new one
    if (tabs.length === 1) {
      const newTab = isAuthenticated && user ? createNewTab() : createLocalTab();
      
      if (isAuthenticated && user && !tab.isLocal) {
        try {
          await supabase
            .from("user_advanced_searches")
            .delete()
            .eq("id", tabId);

          const { data } = await supabase
            .from("user_advanced_searches")
            .insert({
              id: newTab.id,
              user_id: user.id,
              name: "",
              clue_query: "",
              selected_length: null,
              letters: newTab.letters,
              results: [],
              has_searched: false,
              sort_option: "relevance",
              language_filter: 1,
              sort_order: 0,
            })
            .select()
            .single();

          if (data) {
            const row = data as unknown as DbAdvancedSearch;
            setTabs([{ ...newTab, id: row.id }]);
            setActiveTabId(row.id);
            return;
          }
        } catch (error) {
          console.error("Error replacing last tab:", error);
        }
      }
      
      setTabs([newTab]);
      setActiveTabId(newTab.id);
      return;
    }

    // Delete from database if persistent
    if (isAuthenticated && user && !tab.isLocal) {
      try {
        await supabase
          .from("user_advanced_searches")
          .delete()
          .eq("id", tabId);
      } catch (error) {
        console.error("Error deleting tab:", error);
      }
    }

    // Update local state
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    // Switch active tab if needed
    if (activeTabId === tabId) {
      const newActiveIndex = Math.min(tabIndex, newTabs.length - 1);
      setActiveTabId(newTabs[newActiveIndex]?.id || newTabs[0].id);
    }
  }, [tabs, activeTabId, isAuthenticated, user, triggerSave]);

  // Clear all tabs except the active one (batch operation)
  const clearAllTabsExceptActive = useCallback(async () => {
    const tabsToDelete = tabs.filter(t => t.id !== activeTabId);
    if (tabsToDelete.length === 0) return;

    // Remove all links that reference tabs being deleted
    const deletedTabIds = new Set(tabsToDelete.map(t => t.id));
    const updatedLinks = linkGroups
      .map(g => ({
        ...g,
        cells: g.cells.filter(c => !deletedTabIds.has(c.tabId))
      }))
      .filter(g => g.cells.length > 1);

    // Delete from database in batch
    if (isAuthenticated && user) {
      const idsToDelete = tabsToDelete.filter(t => !t.isLocal).map(t => t.id);
      if (idsToDelete.length > 0) {
        try {
          await supabase
            .from("user_advanced_searches")
            .delete()
            .in("id", idsToDelete);
        } catch (error) {
          console.error("Error batch deleting tabs:", error);
        }
      }

      // Update links in database
      try {
        await supabase
          .from("user_advanced_search_links")
          .delete()
          .eq("user_id", user.id);

        if (updatedLinks.length > 0) {
          const linkInserts = updatedLinks.map(link => ({
            user_id: user.id,
            group_id: link.id,
            color: link.color,
            cells: link.cells,
          }));
          await supabase
            .from("user_advanced_search_links")
            .insert(linkInserts);
        }
      } catch (error) {
        console.error("Error updating links:", error);
      }
    }

    // Keep only the active tab
    const activeTab = tabs.find(t => t.id === activeTabId);
    if (activeTab) {
      setTabs([activeTab]);
      setLinkGroups(updatedLinks);
    }
  }, [tabs, activeTabId, linkGroups, isAuthenticated, user]);

  // Get active tab
  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  return {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    setTabs: updateTabs,
    linkGroups,
    setLinkGroups: updateLinkGroups,
    addTab,
    deleteTab,
    clearAllTabsExceptActive,
    isLoading,
    isSaving,
    isAuthenticated,
  };
};
